import { useEffect, useState } from 'react';
import { Col, Row } from 'reactstrap';

import { SimpleInput } from '../../../commonComponents/Input/Input';
import Spinner from '../../../commonComponents/Spinner/Spinner';

/* 
  General notes: each report state can be stored in the redux store
  inside a dictionary of the form {rateId: reportState}. This way
  the gui can be updated dinamically with the results from the api.
*/

export default function ReportByRates({
  // Expects: array
  rates,
  // Expects: string
  reportName,
  // Expects: function
  // Returns: undefined (dispatches action to load reports from api)
  getReports,
  // Expects: function
  // Returns: void (dispatches action to generate report for each selected rate)
  generateReports,
  // Expects: function
  // Returns: boolean (true if the report of a given rate is loading, false otherwise)
  isRateReportLoading,
  // Expects: function
  // Returns: string (link to a report for a given rate)
  getRateReportLink,
  // Expects: function
  // Returns: string (name of a report for a given rate)
  getRateReportName,
}) {
  const [rateSearch, setRateSearch] = useState('');
  const [selectedRates, setSelectedRates] = useState([]);

  useEffect(() => {
    setSelectedRates(
      rates
        .filter((rate) => rate.name !== 'All')
        .map((rate) => ({
          id: rate.id,
          name: rate.name,
          shortname: rate.shortname,
          isSelected: false,
        })),
    );
    getReports();
  }, []);

  const areAllRatesDeselected = selectedRates.every((rate) => !rate.isSelected);

  const changeRateSelection = (rateId) => {
    setSelectedRates(
      selectedRates.map((rate) =>
        rate.id === rateId ? { ...rate, isSelected: !rate.isSelected } : rate,
      ),
    );
  };

  const changeAllRateSelections = (newValue) => {
    setSelectedRates(
      selectedRates.map((rate) => ({ ...rate, isSelected: newValue })),
    );
  };

  const generateReportForSelectedRates = () => {
    generateReports(selectedRates.filter((rate) => rate.isSelected));
    changeAllRateSelections(false);
  };

  const renderSelectAllOrNothing = () => (
    <Row>
      <Col md={12} className="all-or-none">
        <SimpleInput
          type="checkbox"
          description={areAllRatesDeselected ? 'Select All' : 'Deselect All'}
          value={!areAllRatesDeselected}
          onChange={() => changeAllRateSelections(areAllRatesDeselected)}
        />
      </Col>
    </Row>
  );

  const renderRateAndCheckbox = (rate) => (
    <Row key={rate.id}>
      <Col md={12} className="rateRow">
        {isRateReportLoading(rate) && <Spinner />}
        {!isRateReportLoading(rate) && getRateReportLink(rate) && (
          <a href={getRateReportLink(rate)}>
            <span>{getRateReportName(rate)}</span>
          </a>
        )}
        <SimpleInput
          description={`(${rate.shortname}) ${rate.name}`}
          type="checkbox"
          value={rate.isSelected}
          onChange={() => changeRateSelection(rate.id)}
          disabled={isRateReportLoading(rate)}
        />
      </Col>
    </Row>
  );

  return (
    <Row>
      <div className="d-flex w-100">
        <Col>
          <div
            className={
              !areAllRatesDeselected
                ? 'generate-report'
                : 'generate-report disabled'
            }
            onClick={() =>
              !areAllRatesDeselected && generateReportForSelectedRates()
            }
          >
            Generate Report
          </div>
          <div className="report-title">
            {`Select the rates you want included in the ${reportName}`}
          </div>
          <span>Search Rate</span>
          <SimpleInput
            value={rateSearch}
            onChange={(e) => setRateSearch(e.target.value)}
          />

          {renderSelectAllOrNothing()}
          {selectedRates
            .filter((rate) =>
              rateSearch !== ''
                ? `(${rate.shortname}) ${rate.name}`
                    .toLowerCase()
                    .includes(rateSearch.toLowerCase())
                : true,
            )
            .map((rate) => renderRateAndCheckbox(rate))}
        </Col>
      </div>
    </Row>
  );
}
